import type { TokenVerifyResult, SystemToolName } from '@volley/crucible-ipc-types/ipc-channels';
import {
  PREREQ_INSTALL_HINTS,
  commandFor,
  type PrereqTool,
  type SystemToolDiagnostic,
} from '@volley/crucible-ipc-types/system-prereqs';

/**
 * Renders the system-tool prereq status as a list of rows, one per
 * tool, with ✓/✗ + detected version + platform-aware install
 * command. Used by Settings → Diagnostics for the passive listing
 * and by OnboardingOverlay / Dashboard's pre-Create gate when one or
 * more prereq is missing.
 *
 * `mode='full'` shows all three tools regardless of status (Settings).
 * `mode='missing-only'` shows only the rows where `available === false`
 * (Onboarding + pre-Create gate — we don't want to lecture users about
 * the tools they already have).
 *
 * `diagnostics` (optional, issue #309) plumbs in the rich per-tool
 * record fetched via `diagnostics:probe-system-tools`. When provided
 * each row grows a `<details>` expand-row exposing the resolved disk
 * path, shebang, exit-code, and stderr snippet — exactly the same data
 * the support team would see in the pasted diagnostics blob, so an
 * onboarding user can self-diagnose Max's bad-shebang case before
 * having to copy-paste anything to support. Kept INSIDE the
 * `<details>` so the at-a-glance summary stays clean.
 */
export function PrereqStatusList({
  verify,
  mode = 'full',
  diagnostics,
}: {
  verify: Pick<TokenVerifyResult, 'git' | 'node' | 'pnpm'>;
  mode?: 'full' | 'missing-only';
  diagnostics?: Partial<Record<SystemToolName, SystemToolDiagnostic>>;
}): React.JSX.Element | null {
  const platform = typeof navigator !== 'undefined' ? navigator.platform : '';

  const rows: Array<{
    tool: PrereqTool;
    available: boolean;
    version: string | undefined;
    reason: string | undefined;
    minVersion: string | undefined;
    versionWarning: string | undefined;
  }> = [
    {
      tool: 'git',
      available: verify.git.available,
      version: verify.git.version,
      reason: verify.git.reason,
      minVersion: verify.git.minVersion,
      versionWarning: verify.git.versionWarning,
    },
    {
      tool: 'node',
      available: verify.node.available,
      version: verify.node.version,
      reason: verify.node.reason,
      minVersion: verify.node.minVersion,
      versionWarning: verify.node.versionWarning,
    },
    {
      tool: 'pnpm',
      available: verify.pnpm.available,
      version: verify.pnpm.version,
      reason: verify.pnpm.reason,
      minVersion: verify.pnpm.minVersion,
      versionWarning: verify.pnpm.versionWarning,
    },
  ];

  const filtered = mode === 'missing-only' ? rows.filter((r) => !r.available) : rows;

  if (filtered.length === 0) return null;

  return (
    <ul className="flex flex-col gap-3" data-testid="prereq-status-list">
      {filtered.map((row) => {
        const hint = PREREQ_INSTALL_HINTS[row.tool];
        const cmd = commandFor(hint, platform);
        // Issue #2109: a tool that's present but older than Foundry's
        // floor is a distinct state from "missing". Show out-of-date
        // copy + an UPDATE command (not the generic install hint) so the
        // user fixes the right thing.
        const isBelowMinimum = !row.available && row.reason === 'below-minimum';
        return (
          <li
            key={row.tool}
            data-testid={`prereq-row-${row.tool}`}
            data-available={row.available ? 'true' : 'false'}
            data-reason={row.reason ?? undefined}
            className={`rounded-lg border px-3 py-2 text-sm ${
              row.available
                ? 'border-green-500/20 bg-green-500/5'
                : 'border-amber-500/30 bg-amber-500/5'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <span className={row.available ? 'text-green-400' : 'text-amber-400'}>
                  {row.available ? '✓' : '✗'}
                </span>
                <span className="font-medium">{hint.label}</span>
                {row.version && <span className="text-xs text-white/40">v{row.version}</span>}
              </span>
              {!row.available && (
                <a
                  href={hint.docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-crucible-accent hover:underline"
                >
                  {isBelowMinimum ? 'Update instructions' : 'Install instructions'}
                </a>
              )}
            </div>
            {isBelowMinimum ? (
              <>
                <p className="mt-1.5 text-xs text-white/50">
                  Your Corepack{row.version ? ` (v${row.version})` : ''} is older than the v
                  {row.minVersion} Foundry requires. Older Corepack can't download the pinned pnpm
                  version. Update it, then open a fresh terminal:
                </p>
                <code className="mt-2 block rounded bg-black/30 px-2 py-1 text-xs text-white/70">
                  npm install -g corepack@latest && corepack enable
                </code>
              </>
            ) : (
              !row.available && (
                <>
                  <p className="mt-1.5 text-xs text-white/50">{hint.why}</p>
                  {cmd && (
                    <code className="mt-2 block rounded bg-black/30 px-2 py-1 text-xs text-white/70">
                      {cmd}
                    </code>
                  )}
                </>
              )
            )}
            {row.available && row.versionWarning && (
              <p className="mt-1.5 text-xs text-amber-400/80">{row.versionWarning}</p>
            )}
            {diagnostics?.[row.tool] && <SystemToolExpand diagnostic={diagnostics[row.tool]!} />}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Collapsed-by-default expand-row showing the rich diagnostic record
 * for a single tool. Always rendered when the parent supplies the
 * `diagnostics` prop — the at-a-glance check stays visible above, so
 * the expanded view is purely additive triage data. Click to expand.
 *
 * Kept structurally simple (a `<details>` + `<dl>`) for the renderer
 * SSR test — `renderToStaticMarkup` doesn't require interactivity to
 * pin the expanded markup.
 */
function SystemToolExpand({ diagnostic }: { diagnostic: SystemToolDiagnostic }): React.JSX.Element {
  return (
    <details
      className="mt-2 text-xs text-white/60"
      data-testid={`system-tool-diagnostic-${diagnostic.cmd}`}
    >
      <summary className="cursor-pointer text-white/50 hover:text-white/70">
        Diagnostic details
      </summary>
      <dl className="mt-2 space-y-1 rounded bg-black/30 px-2 py-1.5 font-mono">
        <DiagnosticRow label="path">{diagnostic.resolvedPath ?? '(not on PATH)'}</DiagnosticRow>
        {diagnostic.resolverError && (
          <DiagnosticRow label="resolver-error">{diagnostic.resolverError}</DiagnosticRow>
        )}
        {diagnostic.version && <DiagnosticRow label="version">{diagnostic.version}</DiagnosticRow>}
        {diagnostic.shebang && <DiagnosticRow label="shebang">{diagnostic.shebang}</DiagnosticRow>}
        {diagnostic.exitCode !== undefined && diagnostic.exitCode !== null && (
          <DiagnosticRow label="exit-code">{String(diagnostic.exitCode)}</DiagnosticRow>
        )}
        {diagnostic.errno && <DiagnosticRow label="errno">{diagnostic.errno}</DiagnosticRow>}
        {diagnostic.stderrFirstLines && (
          <div>
            <div className="text-white/40">stderr (first 5 lines):</div>
            <pre className="whitespace-pre-wrap break-all text-white/70">
              {diagnostic.stderrFirstLines}
            </pre>
          </div>
        )}
      </dl>
    </details>
  );
}

function DiagnosticRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex gap-2">
      <dt className="shrink-0 text-white/40">{label}:</dt>
      <dd className="break-all text-white/70">{children}</dd>
    </div>
  );
}

/** True when at least one of git/node/pnpm reports `available: false`. */
export function hasMissingPrereqs(
  verify: Pick<TokenVerifyResult, 'git' | 'node' | 'pnpm'>,
): boolean {
  return !verify.git.available || !verify.node.available || !verify.pnpm.available;
}
